import * as Yup from 'yup';
import { createFormConfig, ValidationRules } from 'helpers/createFormConfig';
import { Sizes } from 'components/forms/NumberSelector';
import { ComponentKey } from 'enums/componentKey';
import { InputsConfig } from 'types/form/inputsConfig';
import { LoanConfigWithGuaranteeIdsDto } from 'api/borrower';

export enum BorrowerGuaranteeFieldNames {
  Deductible = "deductible",
  Quota = "quota",
  PsyBuyback = "isPsyBuyback",
  ContribExempt = "isContribExempt",
  IsCapital = "isCapital",
  Guarantees = "guaranteeIds",
}

export type BorrowerGuaranteeFormValues = Omit<LoanConfigWithGuaranteeIdsDto, 'borrowerRoleId' | 'loanId' | 'isActive' | 'borrowerLoanId'>
export type BorrowerGuaranteeFormValuesKeys = Exclude<keyof BorrowerGuaranteeFormValues, 'guaranteeIds'>

export const borrowerGuaranteeInputsConfig: InputsConfig<BorrowerGuaranteeFormValuesKeys> = {
  [BorrowerGuaranteeFieldNames.Quota]: {
    componentKey: ComponentKey.NumberSelector,
    name: BorrowerGuaranteeFieldNames.Quota,
    required: true,
    min: 0,
    max: 100,
    step: 1,
    unitPosition: 'suffix',
    variant: Sizes.md
  },

  [BorrowerGuaranteeFieldNames.Deductible]: {
    componentKey: ComponentKey.NumberSelector,
    name: BorrowerGuaranteeFieldNames.Deductible,
    required: true,
    placeholder: 'Saisir la franchise',
    values: [30, 60, 90, 120, 180],
    variant: Sizes.md
  },

  [BorrowerGuaranteeFieldNames.ContribExempt]: {
    componentKey: ComponentKey.Checkbox,
    name: BorrowerGuaranteeFieldNames.ContribExempt,
    label: 'BORROWER_LOAN_FORM.LABELS.CONTRIBUTION_EXEMPTION',
  },

  [BorrowerGuaranteeFieldNames.PsyBuyback]: {
    componentKey: ComponentKey.Checkbox,
    name: BorrowerGuaranteeFieldNames.PsyBuyback,
    label: 'BORROWER_LOAN_FORM.LABELS.PSY_BUYBACK',
  },

  [BorrowerGuaranteeFieldNames.IsCapital]: {
    componentKey: ComponentKey.RadioGroup,
    name: BorrowerGuaranteeFieldNames.IsCapital,
    label: 'BORROWER_LOAN_FORM.LABELS.IPT_TYPE',
    options: [
      { value: false, label: 'BORROWER_LOAN_FORM.LABELS.RENT' },
      { value: true, label: 'BORROWER_LOAN_FORM.LABELS.CAPITAL' },
    ]
  }
};

export const borrowerGuaranteeValidationRules: ValidationRules<BorrowerGuaranteeFormValuesKeys> = {
  [BorrowerGuaranteeFieldNames.Quota]: Yup.number()
    .nullable()
    .required('La quotité est requise')
    .min(1, 'La quotité minimum est de 1%')
    .max(100, 'La quotité maximum est de 100%')
    .integer('La quotité doit être un nombre entier')
    .typeError('La quotité doit être un nombre valide'),

  [BorrowerGuaranteeFieldNames.Deductible]: Yup.number()
    .nullable()
    .oneOf([30, 60, 90, 120, 180], 'La franchise doit être une valeur prédéfinie (30, 60, 90, 120, 180)')
    .typeError('La franchise doit être un nombre valide'),

  [BorrowerGuaranteeFieldNames.ContribExempt]: Yup.boolean()
    .nullable()
    .oneOf([true, false, null, undefined], 'Valeur invalide'),

  [BorrowerGuaranteeFieldNames.PsyBuyback]: Yup.boolean()
    .nullable()
    .oneOf([true, false, null, undefined], 'Valeur invalide'),

  [BorrowerGuaranteeFieldNames.IsCapital]: Yup.boolean()
    .nullable()
    .oneOf([true, false, null, undefined], 'Valeur invalide')
};

export const borrowerGuaranteeFormConfig = createFormConfig<BorrowerGuaranteeFormValues>(
  borrowerGuaranteeValidationRules as ValidationRules<keyof BorrowerGuaranteeFormValues>,
  {
    [BorrowerGuaranteeFieldNames.Quota]: '100',
    [BorrowerGuaranteeFieldNames.PsyBuyback]: true,
    [BorrowerGuaranteeFieldNames.Deductible]: 90,
  }
);
